import { useState } from "react";
import { PencilLine } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STATUS_CLS = { PENDING: "badge-reserved", APPROVED: "badge-available", REJECTED: "badge-neutral" };

export const OrderAmendments = ({ order, onDone }) => {
  const [open, setOpen] = useState(false);
  const [boxes, setBoxes] = useState({});
  const [instructions, setInstructions] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const w = order.amendment_window;
  if (!w) return null;
  const florida = order.lines.filter((l) => l.source === "FLORIDA");

  const submit = async (e) => {
    e.preventDefault();
    const lines = Object.entries(boxes).filter(([, v]) => v !== "" && v != null).map(([line_id, v]) => ({ line_id, boxes: parseInt(v, 10) }));
    const payload = { reason };
    if (lines.length) payload.lines = lines;
    if (instructions.trim()) payload.delivery_instructions = instructions.trim();
    if (!payload.lines && !payload.delivery_instructions) return toast.error("Describe at least one change");
    setSaving(true);
    try {
      const r = await api.post(`/orders/${order.id}/amendment-request`, payload);
      toast.success(r.data.amendment.window === "LATE" ? "Request sent to Zelligio — outside the 24h window, changes are not guaranteed" : "Change request sent for review");
      setOpen(false); setBoxes({}); setInstructions(""); setReason("");
      onDone?.();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <div className="mt-3" data-testid="order-amendments">
      {w.can_request && (
        <Button variant="ghost" onClick={() => setOpen(true)} data-testid="request-change-btn" className="text-charcoal hover:bg-sand">
          <PencilLine className="mr-1 h-4 w-4" /> Request order change
          <span className="ml-2 text-[10px] uppercase tracking-wide text-warmgrey">{w.in_window ? `until ${new Date(w.window_closes_at).toLocaleString()}` : "after 24h — staff review, not guaranteed"}</span>
        </Button>
      )}
      {w.fulfilment_started && !order.amendments?.length && <p className="text-xs text-warmgrey" data-testid="amend-closed">{["CANCELLED", "REFUNDED", "REFUND_INITIATED", "REFUND_FAILED"].includes(order.status) ? "This order is cancelled or refunded — changes are no longer possible." : "Fulfilment has started — changes are no longer possible."}</p>}
      {order.amendments?.length > 0 && (
        <ul className="mt-2 space-y-1" data-testid="amendment-history">
          {order.amendments.map((a) => (
            <li key={a.id} className="flex flex-wrap items-center gap-2 text-xs text-charcoal" data-testid={`amendment-${a.id}`}>
              <span className={`badge-status ${STATUS_CLS[a.status]}`}>{a.status === "PENDING" ? "Change request under review" : `Change ${a.status.toLowerCase()}`}</span>
              <span className="text-muted-foreground">{new Date(a.requested_at).toLocaleString()} · {a.changes.lines?.map((l) => `${l.sku} ${l.from_boxes}→${l.to_boxes}`).join(", ")}{a.changes.delivery_instructions != null ? " · delivery instructions" : ""}{a.window === "LATE" ? " · after 24h window" : ""}</span>
              {a.resolution_note && <span className="text-muted-foreground">— {a.resolution_note}</span>}
            </li>
          ))}
        </ul>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="amend-dialog">
          <DialogHeader>
            <DialogTitle>Request an order change</DialogTitle>
            <DialogDescription>Zelligio staff review every change after re-checking inventory and payment. Paid amounts are never changed without your confirmation.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            {florida.map((l) => (
              <label key={l.line_id} className="flex items-center justify-between gap-3 text-sm">
                <span className="truncate">{l.sku} · {l.variant_label} <span className="text-muted-foreground">(now {l.boxes} boxes)</span></span>
                <Input type="number" min={1} className="h-9 w-24 bg-white" placeholder={l.boxes} value={boxes[l.line_id] ?? ""} onChange={(e) => setBoxes({ ...boxes, [l.line_id]: e.target.value })} data-testid={`amend-boxes-${l.line_id}`} />
              </label>
            ))}
            <Textarea rows={2} placeholder="New delivery instructions (optional)" value={instructions} onChange={(e) => setInstructions(e.target.value)} data-testid="amend-instructions" />
            <Textarea rows={2} placeholder="Reason" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="amend-reason" />
            <Button type="submit" disabled={saving} className="h-11 w-full bg-terracotta text-white hover:bg-terracotta-hover" data-testid="amend-submit">Send change request</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
